import React, { createContext, useContext, useState, useEffect } from 'react';

// Translation context
const LanguageContext = createContext();

// English translations
const en = {
  // Navigation
  nav: {
    about: "About",
    howItWorks: "How It Works",
    pricing: "Pricing",
    faq: "FAQ",
    contact: "Contact",
    login: "Login",
    uploadCV: "Upload CV",
    forCandidates: "For Candidates",
    forEmployers: "For Employers",
  },
  
  // Homepage
  home: {
    heroTitle: "Medical recruitment —",
    heroTitleHighlight: "led by medical professionals",
    heroSubtitle: "Because we understand what truly matters.",
    heroDescription: "Designed to connect international medical talent with healthcare employers across Saudi Arabia and the Middle East. Founded by medical professionals with over 15 years of clinical and healthcare leadership experience.",
    forMedicalProfessionals: "For Medical Professionals",
    forHospitalsEmployers: "For Hospitals & Employers",
    platformBadge: "Healthcare Recruitment Platform",
    
    // Why Choose section
    whyChooseTitle: "Why Choose Luma Med Global?",
    whyChooseSubtitle: "A recruitment platform built by healthcare professionals who understand what matters",
    
    medicallyLedTitle: "Medically Led",
    medicallyLedDesc: "Founded and operated by healthcare professionals who understand clinical roles and requirements",
    
    aiAssistedTitle: "AI-Assisted Screening",
    aiAssistedDesc: "Technology-enhanced tools to support efficient, fair candidate matching",
    
    verifiedTitle: "Verified Credentials",
    verifiedDesc: "Robust verification processes for qualifications and professional backgrounds",
    
    globalReachTitle: "Global Reach",
    globalReachDesc: "Connecting international talent with opportunities across Saudi Arabia and the Middle East",
    
    // How It Works section
    howItWorksTitle: "How It Works",
    howItWorksSubtitle: "Simple steps to your next healthcare opportunity",
    
    step1Title: "Create Your Profile",
    step1Desc: "Upload your CV and credentials to join our talent pool",
    
    step2Title: "Get Matched",
    step2Desc: "Our AI-assisted tools help match you with suitable opportunities",
    
    step3Title: "Connect & Interview",
    step3Desc: "Engage with employers and complete video interviews",
    
    step4Title: "Start Your Journey",
    step4Desc: "Receive support throughout relocation and onboarding",
    
    // CTA section
    ctaTitle: "Ready to Start Your Journey?",
    ctaSubtitle: "Join healthcare professionals who have found their next opportunity through Luma Med Global",
    ctaCandidateBtn: "Join as a Candidate",
    ctaEmployerBtn: "Register as an Employer",
    
    // Social section
    connectWithUs: "Connect with Luma Med Global",
    followUs: "Follow us for updates, career tips, and healthcare insights",
  },
  
  // About page
  about: {
    title: "About Luma Med Global",
    subtitle: "Medical recruitment led by medical professionals—because we understand what truly matters.",
    
    missionTitle: "Our Mission",
    missionText: "To connect exceptional healthcare professionals with life-changing opportunities in Saudi Arabia and the Middle East, supported by clinical expertise and ethical recruitment practices.",
    
    storyTitle: "Our Story",
    storyText: "Founded by medical professionals with over 15 years of experience across clinical practice and healthcare leadership, Luma Med Global is built with a deep understanding of healthcare systems, regulation, and workforce needs.",
    storyText2: "Having experienced firsthand the challenges of international medical recruitment—outdated processes, lengthy hiring times, and candidates struggling with complex relocation requirements—Luma Med Global was created to address these challenges.",
    storyText3: "We are an early-stage platform focused on building a quality talent pool and connecting healthcare professionals with opportunities in Saudi Arabia and the Middle East, supporting the region's Vision 2030 healthcare transformation.",
    
    valuesTitle: "Our Values",
    medicalExpertiseTitle: "Medical Expertise",
    medicalExpertiseDesc: "Founded by medical professionals with over 15 years of clinical practice and healthcare leadership experience.",
    qualityFocusTitle: "Quality Focus",
    qualityFocusDesc: "Committed to transparency and quality in healthcare recruitment. No shortcuts, no misleading claims.",
    regionalFocusTitle: "Regional Focus",
    regionalFocusDesc: "Designed to connect international medical talent with opportunities across Saudi Arabia and the Middle East.",
    humanCentredTitle: "Human-Centred",
    humanCentredDesc: "Technology enables us, but people are at the heart of everything we do.",
    
    whatWeOfferTitle: "What We Offer",
    offer1Title: "Founded by a Medical Professional",
    offer1Desc: "With over 15 years of clinical and healthcare leadership experience, we understand the medical field from the inside—the competencies that matter, the challenges professionals face, and what healthcare employers need.",
    offer2Title: "Structured Recruitment Workflows",
    offer2Desc: "We provide organised document collection, video interview capabilities, and scheduling tools designed to support efficient recruitment processes.",
    offer3Title: "Information and Guidance",
    offer3Desc: "Resources to help candidates understand SCFHS requirements, visa processes, and relocation considerations for working in Saudi Arabia.",
    offer4Title: "Transparent Approach",
    offer4Desc: "We believe in honest communication. As an early-stage platform, we're building our network of candidates and employers with integrity and transparency.",
    offer5Title: "Vision 2030 Alignment",
    offer5Desc: "We aim to contribute to Saudi Arabia's healthcare transformation by helping connect qualified international medical professionals with opportunities in the Kingdom.",
    
    commitmentTitle: "Our Commitment",
    commitmentText: "Built with transparency, safety, and long-term healthcare workforce quality in mind. Every candidate deserves a clear process, and every employer deserves honest information. That's what we're committed to delivering.",
    
    teamTitle: "Leadership",
    founderRole: "Founder & CEO",
    founderBio: "Over 15 years of clinical and healthcare leadership experience",
  },
  
  // Pricing page
  pricing: {
    title: "Transparent Pricing",
    subtitle: "Flexible options designed for healthcare employers of all sizes",
    
    starterTitle: "Starter",
    starterDesc: "For small clinics and practices",
    
    professionalTitle: "Professional",
    professionalDesc: "For growing healthcare facilities",
    
    enterpriseTitle: "Enterprise",
    enterpriseDesc: "For large hospitals and networks",
    
    perMonth: "/month",
    getStarted: "Get Started",
    contactSales: "Contact Sales",
    
    featuresInclude: "Features include:",
    
    // Common features
    talentPoolAccess: "Access to talent pool",
    candidateProfiles: "candidate profiles",
    aiMatching: "AI-assisted matching",
    messaging: "Direct messaging",
    support: "Support",
    dedicatedManager: "Dedicated account manager",
    customIntegration: "Custom integration",
    
    unlimited: "Unlimited",
    priority: "Priority",
    basic: "Basic",
    
    faqTitle: "Frequently Asked Questions",
  },
  
  // FAQ page
  faq: {
    title: "Frequently Asked Questions",
    subtitle: "Find answers to common questions about Luma Med Global",
    
    candidateQuestions: "For Candidates",
    employerQuestions: "For Employers",
    generalQuestions: "General",
    
    cantFindAnswer: "Can't find what you're looking for?",
    contactUs: "Contact us",
    
    // Sample FAQ items
    q1: "How do I join the talent pool?",
    a1: "Simply click 'Upload CV' and complete the registration form with your professional details and credentials.",
    
    q2: "Is there a fee for candidates?",
    a2: "No, our service is completely free for healthcare professionals. Employers pay for our recruitment services.",
    
    q3: "How long does the verification process take?",
    a3: "Initial profile review typically takes 48-72 hours. Full credential verification timelines vary based on the complexity of qualifications.",
    
    q4: "What regions do you cover?",
    a4: "We specialise in placements across Saudi Arabia and the wider Middle East region.",
  },
  
  // Contact page
  contact: {
    title: "Contact Us",
    subtitle: "We'd love to hear from you. Get in touch with our team.",
    
    getInTouch: "Get in Touch",
    sendMessage: "Send us a Message",
    
    nameLabel: "Full Name",
    emailLabel: "Email Address",
    subjectLabel: "Subject",
    messageLabel: "Your Message",
    submitBtn: "Send Message",
    
    officeTitle: "Our Office",
    emailTitle: "Email",
    phoneTitle: "Phone",
    
    responseTime: "We typically respond within 24-48 hours",
  },
  
  // How It Works - Candidates
  howItWorksCandidates: {
    title: "How It Works for Candidates",
    subtitle: "Your pathway to healthcare opportunities in the Middle East",
    
    step1Title: "Create Your Profile",
    step1Desc: "Register and upload your CV, qualifications, and professional credentials",
    
    step2Title: "Verification",
    step2Desc: "Our team reviews your credentials and verifies your professional background",
    
    step3Title: "Get Matched",
    step3Desc: "AI-assisted tools help match your profile with suitable opportunities",
    
    step4Title: "Interview",
    step4Desc: "Connect with employers through our platform and complete video interviews",
    
    step5Title: "Offer & Onboarding",
    step5Desc: "Receive support throughout the offer process and relocation",
    
    getStarted: "Get Started Today",
    joinTalentPool: "Join Our Talent Pool",
  },
  
  // How It Works - Employers
  howItWorksEmployers: {
    title: "How It Works for Employers",
    subtitle: "Find verified healthcare professionals for your organisation",
    
    step1Title: "Register Your Organisation",
    step1Desc: "Create an employer account and tell us about your hiring needs",
    
    step2Title: "Access Talent Pool",
    step2Desc: "Browse our database of verified healthcare professionals",
    
    step3Title: "Connect with Candidates",
    step3Desc: "Use our messaging system to engage with potential hires",
    
    step4Title: "Interview & Hire",
    step4Desc: "Conduct interviews and make offers through our platform",
    
    getStarted: "Get Started Today",
    registerNow: "Register as an Employer",
  },
  
  // Footer
  footer: {
    tagline: "Medical recruitment led by medical professionals—because we understand what truly matters.",
    quickLinks: "Quick Links",
    legal: "Legal",
    contactUs: "Contact Us",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    gdprCompliance: "GDPR Compliance",
    aiTransparency: "AI Transparency",
    joinWaitlist: "Join Waitlist",
    allRightsReserved: "All rights reserved.",
  },
  
  // Common
  common: {
    learnMore: "Learn More",
    viewAll: "View All",
    submit: "Submit",
    cancel: "Cancel",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    yes: "Yes",
    no: "No",
  },
};

// Arabic translations
const ar = {
  // Navigation
  nav: {
    about: "من نحن",
    howItWorks: "كيف يعمل",
    pricing: "الأسعار",
    faq: "الأسئلة الشائعة",
    contact: "اتصل بنا",
    login: "تسجيل الدخول",
    uploadCV: "رفع السيرة الذاتية",
    forCandidates: "للمرشحين",
    forEmployers: "لأصحاب العمل",
  },
  
  // Homepage
  home: {
    heroTitle: "توظيف طبي —",
    heroTitleHighlight: "بقيادة متخصصين طبيين",
    heroSubtitle: "لأننا نفهم ما يهم حقاً.",
    heroDescription: "منصة مصممة لربط الكفاءات الطبية الدولية بأصحاب العمل في المملكة العربية السعودية والشرق الأوسط. تأسست بواسطة متخصصين طبيين يمتلكون أكثر من 15 عاماً من الخبرة السريرية والقيادية في مجال الرعاية الصحية.",
    forMedicalProfessionals: "للمتخصصين الطبيين",
    forHospitalsEmployers: "للمستشفيات وأصحاب العمل",
    platformBadge: "منصة توظيف الرعاية الصحية",
    
    // Why Choose section
    whyChooseTitle: "لماذا تختار لوما ميد جلوبال؟",
    whyChooseSubtitle: "منصة توظيف بناها متخصصون في الرعاية الصحية يفهمون ما يهم",
    
    medicallyLedTitle: "بقيادة طبية",
    medicallyLedDesc: "تأسست وتدار بواسطة متخصصين في الرعاية الصحية يفهمون الأدوار والمتطلبات السريرية",
    
    aiAssistedTitle: "فحص مدعوم بالذكاء الاصطناعي",
    aiAssistedDesc: "أدوات معززة بالتكنولوجيا لدعم مطابقة المرشحين بكفاءة وعدالة",
    
    verifiedTitle: "أوراق اعتماد موثقة",
    verifiedDesc: "عمليات تحقق قوية للمؤهلات والخلفيات المهنية",
    
    globalReachTitle: "وصول عالمي",
    globalReachDesc: "ربط الكفاءات الدولية بالفرص في المملكة العربية السعودية والشرق الأوسط",
    
    // How It Works section
    howItWorksTitle: "كيف يعمل",
    howItWorksSubtitle: "خطوات بسيطة نحو فرصتك القادمة في الرعاية الصحية",
    
    step1Title: "أنشئ ملفك الشخصي",
    step1Desc: "ارفع سيرتك الذاتية وأوراق اعتمادك للانضمام إلى مجموعة المواهب لدينا",
    
    step2Title: "احصل على المطابقة",
    step2Desc: "أدواتنا المدعومة بالذكاء الاصطناعي تساعد في مطابقتك مع الفرص المناسبة",
    
    step3Title: "تواصل وأجرِ المقابلة",
    step3Desc: "تواصل مع أصحاب العمل وأكمل مقابلات الفيديو",
    
    step4Title: "ابدأ رحلتك",
    step4Desc: "احصل على الدعم طوال عملية الانتقال والتأهيل",
    
    // CTA section
    ctaTitle: "هل أنت مستعد لبدء رحلتك؟",
    ctaSubtitle: "انضم إلى المتخصصين في الرعاية الصحية الذين وجدوا فرصتهم القادمة من خلال لوما ميد جلوبال",
    ctaCandidateBtn: "انضم كمرشح",
    ctaEmployerBtn: "سجل كصاحب عمل",
    
    // Social section
    connectWithUs: "تواصل مع لوما ميد جلوبال",
    followUs: "تابعنا للحصول على التحديثات ونصائح المهنة ورؤى الرعاية الصحية",
  },
  
  // About page
  about: {
    title: "عن لوما ميد جلوبال",
    subtitle: "توظيف طبي بقيادة متخصصين طبيين — لأننا نفهم ما يهم حقاً.",
    
    missionTitle: "مهمتنا",
    missionText: "ربط المتخصصين المتميزين في الرعاية الصحية بفرص تغير الحياة في المملكة العربية السعودية والشرق الأوسط، مدعومين بالخبرة السريرية وممارسات التوظيف الأخلاقية.",
    
    storyTitle: "قصتنا",
    storyText: "تأسست لوما ميد جلوبال على يد متخصصين طبيين يمتلكون أكثر من 15 عاماً من الخبرة في الممارسة السريرية والقيادة الصحية، بفهم عميق لأنظمة الرعاية الصحية واللوائح واحتياجات القوى العاملة.",
    storyText2: "بعد تجربة تحديات التوظيف الطبي الدولي بشكل مباشر — العمليات القديمة، وأوقات التوظيف الطويلة، والمرشحون الذين يعانون من متطلبات النقل المعقدة — تم إنشاء لوما ميد جلوبال لمعالجة هذه التحديات.",
    storyText3: "نحن منصة في مرحلة مبكرة نركز على بناء مجموعة مواهب عالية الجودة وربط المتخصصين في الرعاية الصحية بالفرص في المملكة العربية السعودية والشرق الأوسط، دعماً لتحول الرعاية الصحية في رؤية 2030.",
    
    valuesTitle: "قيمنا",
    medicalExpertiseTitle: "الخبرة الطبية",
    medicalExpertiseDesc: "تأسست على يد متخصصين طبيين يمتلكون أكثر من 15 عاماً من الخبرة في الممارسة السريرية والقيادة الصحية.",
    qualityFocusTitle: "التركيز على الجودة",
    qualityFocusDesc: "ملتزمون بالشفافية والجودة في توظيف الرعاية الصحية. لا اختصارات ولا ادعاءات مضللة.",
    regionalFocusTitle: "التركيز الإقليمي",
    regionalFocusDesc: "مصممة لربط الكفاءات الطبية الدولية بالفرص في المملكة العربية السعودية والشرق الأوسط.",
    humanCentredTitle: "التركيز على الإنسان",
    humanCentredDesc: "التكنولوجيا تمكننا، لكن الناس هم في صميم كل ما نقوم به.",
    
    whatWeOfferTitle: "ما نقدمه",
    offer1Title: "تأسست بواسطة متخصص طبي",
    offer1Desc: "مع أكثر من 15 عاماً من الخبرة السريرية والقيادية في الرعاية الصحية، نفهم المجال الطبي من الداخل — الكفاءات المهمة، والتحديات التي يواجهها المتخصصون، وما يحتاجه أصحاب العمل في الرعاية الصحية.",
    offer2Title: "سير عمل توظيف منظم",
    offer2Desc: "نوفر جمع مستندات منظم، وقدرات مقابلات الفيديو، وأدوات الجدولة المصممة لدعم عمليات التوظيف الفعالة.",
    offer3Title: "المعلومات والإرشاد",
    offer3Desc: "موارد لمساعدة المرشحين على فهم متطلبات SCFHS وعمليات التأشيرة واعتبارات النقل للعمل في المملكة العربية السعودية.",
    offer4Title: "نهج شفاف",
    offer4Desc: "نؤمن بالتواصل الصادق. كمنصة في مرحلة مبكرة، نبني شبكتنا من المرشحين وأصحاب العمل بنزاهة وشفافية.",
    offer5Title: "التوافق مع رؤية 2030",
    offer5Desc: "نهدف إلى المساهمة في تحول الرعاية الصحية في المملكة العربية السعودية من خلال المساعدة في ربط المتخصصين الطبيين الدوليين المؤهلين بالفرص في المملكة.",
    
    commitmentTitle: "التزامنا",
    commitmentText: "بُني بالشفافية والسلامة وجودة القوى العاملة الصحية على المدى الطويل. كل مرشح يستحق عملية واضحة، وكل صاحب عمل يستحق معلومات صادقة. هذا ما نلتزم بتقديمه.",
    
    teamTitle: "القيادة",
    founderRole: "المؤسس والرئيس التنفيذي",
    founderBio: "أكثر من 15 عاماً من الخبرة السريرية والقيادية في الرعاية الصحية",
  },
  
  // Pricing page
  pricing: {
    title: "أسعار شفافة",
    subtitle: "خيارات مرنة مصممة لأصحاب العمل في الرعاية الصحية من جميع الأحجام",
    
    starterTitle: "المبتدئ",
    starterDesc: "للعيادات والممارسات الصغيرة",
    
    professionalTitle: "المهني",
    professionalDesc: "للمرافق الصحية النامية",
    
    enterpriseTitle: "المؤسسات",
    enterpriseDesc: "للمستشفيات والشبكات الكبيرة",
    
    perMonth: "/شهرياً",
    getStarted: "ابدأ الآن",
    contactSales: "اتصل بالمبيعات",
    
    featuresInclude: "الميزات تشمل:",
    
    // Common features
    talentPoolAccess: "الوصول إلى مجموعة المواهب",
    candidateProfiles: "ملفات المرشحين",
    aiMatching: "مطابقة مدعومة بالذكاء الاصطناعي",
    messaging: "الرسائل المباشرة",
    support: "الدعم",
    dedicatedManager: "مدير حساب مخصص",
    customIntegration: "تكامل مخصص",
    
    unlimited: "غير محدود",
    priority: "أولوية",
    basic: "أساسي",
    
    faqTitle: "الأسئلة الشائعة",
  },
  
  // FAQ page
  faq: {
    title: "الأسئلة الشائعة",
    subtitle: "اعثر على إجابات للأسئلة الشائعة حول لوما ميد جلوبال",
    
    candidateQuestions: "للمرشحين",
    employerQuestions: "لأصحاب العمل",
    generalQuestions: "عام",
    
    cantFindAnswer: "لم تجد ما تبحث عنه؟",
    contactUs: "اتصل بنا",
    
    // Sample FAQ items
    q1: "كيف أنضم إلى مجموعة المواهب؟",
    a1: "ببساطة انقر على 'رفع السيرة الذاتية' وأكمل نموذج التسجيل بتفاصيلك المهنية وأوراق اعتمادك.",
    
    q2: "هل هناك رسوم للمرشحين؟",
    a2: "لا، خدمتنا مجانية تماماً للمتخصصين في الرعاية الصحية. أصحاب العمل يدفعون مقابل خدمات التوظيف لدينا.",
    
    q3: "كم تستغرق عملية التحقق؟",
    a3: "مراجعة الملف الأولية تستغرق عادةً 48-72 ساعة. جداول التحقق الكامل من أوراق الاعتماد تختلف بناءً على تعقيد المؤهلات.",
    
    q4: "ما المناطق التي تغطونها؟",
    a4: "نحن متخصصون في التوظيف في المملكة العربية السعودية ومنطقة الشرق الأوسط الأوسع.",
  },
  
  // Contact page
  contact: {
    title: "اتصل بنا",
    subtitle: "يسعدنا أن نسمع منك. تواصل مع فريقنا.",
    
    getInTouch: "تواصل معنا",
    sendMessage: "أرسل لنا رسالة",
    
    nameLabel: "الاسم الكامل",
    emailLabel: "عنوان البريد الإلكتروني",
    subjectLabel: "الموضوع",
    messageLabel: "رسالتك",
    submitBtn: "إرسال الرسالة",
    
    officeTitle: "مكتبنا",
    emailTitle: "البريد الإلكتروني",
    phoneTitle: "الهاتف",
    
    responseTime: "نرد عادةً خلال 24-48 ساعة",
  },
  
  // How It Works - Candidates
  howItWorksCandidates: {
    title: "كيف يعمل للمرشحين",
    subtitle: "طريقك إلى فرص الرعاية الصحية في الشرق الأوسط",
    
    step1Title: "أنشئ ملفك الشخصي",
    step1Desc: "سجل وارفع سيرتك الذاتية ومؤهلاتك وأوراق اعتمادك المهنية",
    
    step2Title: "التحقق",
    step2Desc: "يراجع فريقنا أوراق اعتمادك ويتحقق من خلفيتك المهنية",
    
    step3Title: "احصل على المطابقة",
    step3Desc: "أدوات مدعومة بالذكاء الاصطناعي تساعد في مطابقة ملفك مع الفرص المناسبة",
    
    step4Title: "المقابلة",
    step4Desc: "تواصل مع أصحاب العمل عبر منصتنا وأكمل مقابلات الفيديو",
    
    step5Title: "العرض والتأهيل",
    step5Desc: "احصل على الدعم طوال عملية العرض والانتقال",
    
    getStarted: "ابدأ اليوم",
    joinTalentPool: "انضم إلى مجموعة المواهب",
  },
  
  // How It Works - Employers
  howItWorksEmployers: {
    title: "كيف يعمل لأصحاب العمل",
    subtitle: "اعثر على متخصصين معتمدين في الرعاية الصحية لمنظمتك",
    
    step1Title: "سجل منظمتك",
    step1Desc: "أنشئ حساب صاحب عمل وأخبرنا عن احتياجاتك التوظيفية",
    
    step2Title: "الوصول إلى مجموعة المواهب",
    step2Desc: "تصفح قاعدة بياناتنا من المتخصصين المعتمدين في الرعاية الصحية",
    
    step3Title: "تواصل مع المرشحين",
    step3Desc: "استخدم نظام الرسائل لدينا للتواصل مع المرشحين المحتملين",
    
    step4Title: "قابل ووظف",
    step4Desc: "أجرِ المقابلات وقدم العروض من خلال منصتنا",
    
    getStarted: "ابدأ اليوم",
    registerNow: "سجل كصاحب عمل",
  },
  
  // Footer
  footer: {
    tagline: "توظيف طبي بقيادة متخصصين طبيين — لأننا نفهم ما يهم حقاً.",
    quickLinks: "روابط سريعة",
    legal: "قانوني",
    contactUs: "اتصل بنا",
    privacyPolicy: "سياسة الخصوصية",
    termsOfService: "شروط الخدمة",
    gdprCompliance: "الامتثال للائحة حماية البيانات",
    aiTransparency: "شفافية الذكاء الاصطناعي",
    joinWaitlist: "انضم لقائمة الانتظار",
    allRightsReserved: "جميع الحقوق محفوظة.",
  },
  
  // Common
  common: {
    learnMore: "اعرف المزيد",
    viewAll: "عرض الكل",
    submit: "إرسال",
    cancel: "إلغاء",
    save: "حفظ",
    edit: "تعديل",
    delete: "حذف",
    loading: "جاري التحميل...",
    error: "خطأ",
    success: "نجاح",
    yes: "نعم",
    no: "لا",
  },
};

const translations = { en, ar };

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    if (typeof window !== 'undefined') {
      return localStorage.getItem('luma-language') || 'en';
    }
    return 'en';
  });

  const [isRTL, setIsRTL] = useState(language === 'ar');

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('luma-language', language);
    
    // Update RTL
    const rtl = language === 'ar';
    setIsRTL(rtl);
    
    // Update document direction
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Add/remove RTL class for styling
    if (rtl) {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && value[fallbackKey]) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if translation not found
          }
        }
        break;
      }
    }
    
    return value;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const value = {
    language,
    setLanguage,
    isRTL,
    t,
    toggleLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
